<template>
  <div class="hooks-filter-bar z-10 sticky top-0 flex items-center gap-2 px-4 py-3 bg-white border-b border-border-normal">
    <LiveStatusDropdown
      :live-status="liveStatus"
      @update:live-status="$emit('update:live-status', $event)"
    />
    <div class="px-1 h-3">
      <div class="border-l border-border-normal h-full" />
    </div>
    <div class="flex-grow flex items-center">
      <div class="search-input-container group py-1.5 px-2 flex flex-grow items-center transition rounded-md"
      @mouseenter="searchHovered = true" @mouseleave="searchHovered = false"
      style="min-width: 200px; max-width: 682px;">
        <!-- Search Image -->
        <div class="w-5 h-5">
          <SearchIcon class="transition-colors ease-in-out"
          :class="searchFocused ? 'text-icon-normal' : 'text-text-subdued group-hover:text-icon-muted'"/>
        </div>
        <!-- Text Input -->
        <input ref="searchInput" :value="textSearch" placeholder="Search Hooks..." 
        @input="$emit('update:textSearch', $event.target.value)" 
        @focus="searchFocused = true" @blur="searchFocused = false"
        @keydown.enter.prevent="searchSubmit"
        class="ml-2 text-sm rounded flex items-center outline-none transition-all border-none w-full 
        focus:border-none text-text-muted bg-white bg-opacity-0">
        <transition name="slow-fade">
          <button v-if="showClearSearchButton" class="w-5 h-5 text-icon-normal transition-colors hover:text-icon-muted"
          @click="$emit('update:textSearch', '')">
            <BoardRemoveIcon :width="18" :height="18" />
          </button>
        </transition>
      </div>
    </div>
    <!-- spacer -->
    <div class="flex-grow" />
    <!-- Date range -->
    <div ref="dateRoot" class="relative">
      <button class="dropdown-button flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md" :class="{ 'expanded': dateOpen }"
      @click="dateOpen = !dateOpen">
        <CalendarIcon :width="20" :height="20" class="text-icon-normal" />
        <BaseText type="label" size="sm" class="text-text-muted">
          {{ dateLabel }}
        </BaseText>
      </button>
      <transition>
        <div v-if="dateOpen" class="absolute right-0 mt-2 z-50" v-on-clickaway="() => { dateOpen = false }">
          <DateRangeSelectorDropdown 
            :selected-date-range="selectedDateRange"
            close-on-apply
            @update:selectedDateRange="onDateRangeChange"
            @close="dateOpen = false"
          />
        </div>
      </transition>
    </div>
    <!-- Sort dropdown -->
    <SortDropdown
      :selected-sort="sort"
      :sort-categories="sortCategories"
      @select-sort="$emit('update:sort', $event)"
    />
    <!-- Export menu -->
    <HooksExportMenu
      class="flex-shrink-0"
      @export="$emit('export', $event)"
    />
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'
import { getMatchingPresetName } from '@/utils/dateUtils'
import DateRangeSelectorDropdown from '../../search/TopBarComponents/DateRangeSelectorDropdown.vue'
import LiveStatusDropdown from './LiveStatusDropdown.vue'
import SortDropdown from '../../search/TopBarComponents/SortDropdown.vue'
import HooksExportMenu from './HooksExportMenu.vue'

// Icons
import SearchIcon from '../../globals/Icons/SearchIcon.vue'
import CalendarIcon from '../../globals/Icons/CalendarIcon.vue'
import AdsCarouselIcon from '../../globals/Icons/AdsCarouselIcon.vue'
import BoardRemoveIcon from '../../globals/Icons/BoardRemoveIcon.vue'

export default {
  name: 'HooksFilterBar',
  mixins: [clickaway],
  components: {
    LiveStatusDropdown,
    SortDropdown,
    HooksExportMenu,
    DateRangeSelectorDropdown,
    SearchIcon,
    CalendarIcon,
    BoardRemoveIcon
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    textSearch: {
      type: String,
      required: true
    },
    liveStatus: {
      type: Array,
      required: true
    },
    sort: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      dateOpen: false,
      searchFocused: false,
      searchHovered: false,
      sortCategories: [
        {
          label: 'Date',
          icon: CalendarIcon,
          options: [
            { value: 'most_recent', name: 'Most Recent' },
            { value: 'oldest', name: 'Oldest' }
          ]
        },
        {
          label: 'Ads',
          icon: AdsCarouselIcon,
          options: [
            { value: 'longest_running', name: 'Longest Running' },
            { value: 'most_duplicates', name: 'Most Duplicates' }
          ]
        }
      ]
    }
  },
  computed: {
    selectedDateRange () {
      return this.filters?.selectedDateRange || null
    },
    dateLabel () {
      const range = this.selectedDateRange
      if (!range || !range.start || !range.end) return 'All time'
      return getMatchingPresetName(range) || 'Custom Range'
    },
    showClearSearchButton () {
      return this.textSearch.length > 0 && this.searchHovered && !this.searchFocused
    }
  },
  methods: {
    onDateRangeChange (range) {
      // Bubbles to HooksBrandView's local selectedDateRange; AdvertisementHooksList's
      // filtersKey watcher then refetches with ranBetweenStart/End.
      this.$emit('update:dateRange', range)
      this.dateOpen = false
    },
    searchSubmit () {
      this.$emit('update:textSearch', this.textSearch)
      this.$refs.searchInput.blur()
    }
  }
}
</script>

<style scoped>
.search-input-container {
  --placeholder-color: #5E6678;
  transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
  background-color: transparent;
  box-shadow: none;
}
.search-input-container:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  background-color: white;
  --placeholder-color: black;
}
.search-input-container:focus-within {
  box-shadow: none;
  background-color: transparent;
  --placeholder-color: #A4ACB9;
}
.search-input-container input::placeholder {
  transition: color 150ms ease-in-out;
  color: var(--placeholder-color);
}

.hooks-filter-bar ::v-deep .dropdown-button {
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  background-color: transparent;
  box-shadow: none;
  white-space: nowrap;
}
.hooks-filter-bar ::v-deep .dropdown-button:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  background-color: white;
}
.hooks-filter-bar ::v-deep .dropdown-button.expanded {
  box-shadow: none;
  background-color: #F6F8FA;
}

.v-enter-active, .v-leave-active {
  transition: opacity 75ms ease-in-out;
}
.slow-fade-enter-active, .slow-fade-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to, .slow-fade-enter-from, .slow-fade-enter, .slow-fade-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from, .slow-fade-enter-to, .slow-fade-leave-from {
  opacity: 1;
}
</style>